import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Paginacao } from "@/components/Paginacao";
import { useDebounce, usePaginado } from "@/hooks/usePaginado";
import { RoleGuard } from "@/components/RoleGuard";
import { PageHeader } from "@/components/padroes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import type { Departamento } from "@/data/mock";
import type { Headcount, ResumoGrupoHeadcount } from "@/data/dashboard";
import { toast } from "sonner";
import { Plus, Trash2, Layers, Search, Loader2 } from "lucide-react";

const RHDepartamentos = () => {
  const [busca, setBusca] = useState("");
  const buscaAtrasada = useDebounce(busca.trim());
  const {
    items: departamentos,
    total,
    page,
    pageSize,
    carregando,
    setPage,
    recarregar: load,
  } = usePaginado<Departamento>("/departamentos", { q: buscaAtrasada });
  const [depName, setDepName] = useState("");
  const [savingDep, setSavingDep] = useState(false);
  /**
   * Pessoas ativas por departamento, do Headcount. O cadastro do usuário guarda
   * o nome do departamento (texto), então a chave é o nome em minúsculas.
   */
  const [pessoas, setPessoas] = useState<Map<string, ResumoGrupoHeadcount> | null>(null);

  useEffect(() => {
    api
      .get<Headcount>("/dashboard/headcount")
      .then((h) => setPessoas(new Map(h.porDepartamento.map((g) => [g.chave, g]))))
      .catch(() => setPessoas(null));
  }, []);

  const handleCreateDep = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!depName.trim()) { toast.error("Informe o nome do departamento"); return; }
    setSavingDep(true);
    try {
      await api.post("/departamentos", { name: depName.trim() });
      toast.success("Departamento criado");
      setDepName("");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao criar departamento");
    } finally {
      setSavingDep(false);
    }
  };

  const handleDeleteDep = async (id: string) => {
    try {
      await api.delete(`/departamentos/${id}`);
      toast.success("Departamento removido");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao remover");
    }
  };

  const colunas = pessoas ? 5 : 2;

  return (
    <RoleGuard role="rh">
      <AppLayout title="Departamentos">
        <div className="mx-auto flex max-w-7xl flex-col gap-4">
          <PageHeader titulo="Departamentos" subtitulo="Usados para agrupar custo e headcount." />

          <div className="flex flex-wrap items-start gap-4">
            <section className="flex flex-[1_1_300px] flex-col gap-3.5 rounded-xl border bg-card px-5 py-[18px]">
              <h3 className="flex items-center gap-2 text-[15px] font-semibold">
                <Plus className="h-4 w-4 text-primary" /> Novo departamento
              </h3>
              <form onSubmit={handleCreateDep} className="flex flex-col gap-3.5">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="depName">Nome *</Label>
                  <Input
                    id="depName"
                    value={depName}
                    onChange={(e) => setDepName(e.target.value)}
                    placeholder="Ex.: Tecnologia"
                  />
                </div>
                <Button type="submit" disabled={savingDep} className="self-start">
                  {savingDep && <Loader2 className="animate-spin" />}
                  Adicionar
                </Button>
              </form>
            </section>

            <div className="flex min-w-0 flex-[2_1_520px] flex-col gap-3">
              <section className="overflow-hidden rounded-xl border bg-card">
                <div className="border-b border-border-soft p-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      placeholder="Buscar por nome"
                      className="h-9 pl-9"
                      aria-label="Buscar departamentos"
                    />
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Departamento</TableHead>
                      {pessoas && (
                        <>
                          <TableHead className="text-right">Pessoas</TableHead>
                          <TableHead className="text-right">PJ</TableHead>
                          <TableHead className="text-right">CLT</TableHead>
                        </>
                      )}
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {departamentos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={colunas} className="py-12 text-center text-muted-foreground">
                          <Layers className="mx-auto mb-2 h-8 w-8 opacity-30" />
                          {carregando
                            ? "Carregando..."
                            : buscaAtrasada
                              ? "Nenhum departamento encontrado"
                              : "Nenhum departamento cadastrado"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      departamentos.map((dep) => {
                        const g = pessoas?.get(dep.name.trim().toLowerCase());
                        return (
                          <TableRow key={dep.id}>
                            <TableCell className="font-medium">{dep.name}</TableCell>
                            {pessoas && (
                              <>
                                <TableCell className="text-right font-semibold">{g?.total ?? 0}</TableCell>
                                <TableCell className="text-right">{g?.pj ?? 0}</TableCell>
                                <TableCell className="text-right">{g?.clt ?? 0}</TableCell>
                              </>
                            )}
                            <TableCell className="px-2 text-center">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    aria-label={`Remover ${dep.name}`}
                                  >
                                    <Trash2 className="h-[15px] w-[15px]" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Remover departamento?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      O departamento <strong>{dep.name}</strong> será removido permanentemente.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      onClick={() => handleDeleteDep(dep.id)}
                                    >
                                      Remover
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </section>
              <Paginacao
                page={page}
                pageSize={pageSize}
                total={total}
                onPage={setPage}
                carregando={carregando}
                rotulo="departamentos"
              />
            </div>
          </div>
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default RHDepartamentos;
